/**
 * nTitle
 * nContent
 * nType = info
 * nDelay = 300
 * nIcon = check
 * nAlign = topRight
 */
import { call, fork, put, delay } from 'redux-saga/effects';
import isInteger from 'lodash/isInteger';
import parseInt from 'lodash/parseInt';
import pick from 'lodash/pick';
import React from 'react';
import { isMobile } from '@ts/utils/browser';
import { ToastContent, callToast } from '@ts/commons/Toast';
import actions from '../actions';

const MOUNT_DELAY = 300;
const DEFAULTS = {
    nType: 'info',
    nDelay: 1000,
    nIcon: undefined,
    nAlign: 'topRight',
    nDuration: 4500,
    nMobile: 1,
    nLink: undefined,
    nButton: undefined,
};

const NTYPES = ['info', 'success', 'warning', 'error'];
const NALIGNS = ['topRight', 'bottomRight', 'bottomLeft', 'topLeft'];

export function checkRequiredArguments(urlSearchParams) {
    const nTitle = urlSearchParams.get('nTitle');
    const nContent = urlSearchParams.get('nContent');

    return (
        typeof nTitle === 'string' &&
        typeof nContent === 'string' &&
        nTitle.trim().length > 0 &&
        nContent.trim().length > 0
    );
}

export function computeArguments(urlSearchParams) {
    const nextArguments = Object.assign({}, DEFAULTS, {
        nTitle: urlSearchParams.get('nTitle'),
        nContent: urlSearchParams.get('nContent'),
        nDelay: urlSearchParams.get('nDelay'),
        nDuration: urlSearchParams.get('nDuration'),
        nMobile: urlSearchParams.get('nMobile'),
        nType: urlSearchParams.get('nType'),
        nAlign: urlSearchParams.get('nAlign'),
        nIcon: urlSearchParams.get('nIcon'),
        nLink: urlSearchParams.get('nLink'),
        nButton: urlSearchParams.get('nButton'),
    });

    nextArguments.nTitle = decodeURIComponent(nextArguments.nTitle);
    nextArguments.nContent = decodeURIComponent(nextArguments.nContent);

    nextArguments.nDelay = parseInt(nextArguments.nDelay);
    nextArguments.nDuration = parseInt(nextArguments.nDuration);
    nextArguments.nMobile = parseInt(nextArguments.nMobile);

    if (NTYPES.indexOf(nextArguments.nType) <= -1) {
        nextArguments.nType = DEFAULTS.nType;
    }

    if (NALIGNS.indexOf(nextArguments.nAlign) <= -1) {
        nextArguments.nAlign = DEFAULTS.nAlign;
    }

    if (
        typeof nextArguments.nIcon !== 'string' ||
        nextArguments.nIcon.length <= 0 ||
        nextArguments.nIcon.length >= 64
    ) {
        nextArguments.nIcon = DEFAULTS.nIcon;
    }

    if (!isInteger(nextArguments.nDelay) || nextArguments.nDelay < 0) {
        nextArguments.nDelay = DEFAULTS.nDelay;
    }

    if (nextArguments.nDelay > 100000) {
        nextArguments.nDelay = 100000;
    }

    if (!isInteger(nextArguments.nDuration) || nextArguments.nDuration < 0) {
        nextArguments.nDuration = DEFAULTS.nDuration;
    }

    if (nextArguments.nDuration > 30000) {
        nextArguments.nDuration = 30000;
    }

    if ([0, 1].indexOf(nextArguments.nMobile) <= -1) {
        nextArguments.nMobile = DEFAULTS.nMobile;
    }

    return pick(nextArguments, ['nTitle', 'nContent', ...Object.keys(DEFAULTS)]);
}

export function* showByParsedLocationArguments() {
    try {
        const locationArguments = new URLSearchParams(window.location.search);
        if (!checkRequiredArguments(locationArguments)) {
            return;
        }

        const validArguments = computeArguments(locationArguments);
        yield fork(showNotify, validArguments);
    } catch (err) {
        yield put(actions.app.error(err));
    }
}

export function* showNotify(notifyArguments) {
    try {
        if (notifyArguments.nMobile === 0 && isMobile()) {
            return;
        }

        yield delay(notifyArguments.nDelay);

        callToast(
            <ToastContent
                header={notifyArguments.nTitle}
                text={notifyArguments.nContent}
                icon={notifyArguments.nIcon}
                link={notifyArguments.nLink}
                button={notifyArguments.nButton}
            />,
            { autoClose: notifyArguments.nDuration }
        );
    } catch (err) {
        yield put(actions.app.error(err));
    }
}

export default function* locationNotifySaga() {
    yield delay(MOUNT_DELAY);
    yield call(showByParsedLocationArguments);
}
